import type { AxiosRequestConfig } from 'axios';
import { directApiInstance } from './httpClient';

/**
 * SAM-1298 — business requests (samUserManagement, under the existing /sam proxy): a business asks, the tenant
 * approves, it goes live. Contract: Logic, 2 Nov (side note «sam-1298-business-requests-contrato»).
 *
 *   POST /sam/business-requests                    {type, kind, target_id?, business|service|ad|employee}
 *   GET  /sam/business-requests?mine=1             the sender's own requests (Request sent)
 *   GET  /sam/business-requests                    the tenant's inbox (the tenant Front Desk gets type=employee only)
 *   GET  /sam/business-requests/{id}               detail: full payload, `current` for an edit, `history`
 *   POST /sam/business-requests/{id}/approve | reject {note}
 *   GET  /sam/business-requests/options            categories and estate groups (with has_admin_business)
 *
 * Stored status is sent | approved | rejected | withdrawn; the screens read `ui_status`, computed by the server.
 * Employee requests are Team v3: the person and the assignment exist from the moment it is sent.
 */

export type RequestType = 'business' | 'service' | 'employee' | 'ad';
export type RequestKind = 'new' | 'edit';
export type RequestStatus = 'sent' | 'approving' | 'approved' | 'rejected' | 'withdrawn';
export type RequestUiStatus = 'processing' | 'approved' | 'live' | 'denied' | 'withdrawn';

export interface BusinessRequest {
  id: string;
  type: RequestType;
  kind: RequestKind;
  status: RequestStatus;
  ui_status: RequestUiStatus;
  /** A Business request of the Estate category; `estate_group_id` is then the group it would administer. */
  is_estate: boolean;
  estate_group_id: number | null;
  business_id: string | null;
  business_name: string | null;
  target_id: string | null;
  /** Business, service or ad name, or the person's name. */
  title: string | null;
  /** What the business sent; for an edit, only the changed fields. Not in the lists. */
  payload?: Record<string, unknown> | null;
  /** Detail of an edit: the live values of the changed fields. */
  current?: Record<string, unknown> | null;
  note: string | null;
  submitted_by: { sub?: string; name: string | null; email: string | null } | null;
  sent_at: string | null;
  reviewed_at: string | null;
  employee?: { employee_id: string; assignment_id: string } | null;
  history?: Array<{ action: string; actor: string | null; note: string | null; at: string }>;
}

export interface RequestOptions {
  categories: Array<{ id: number; key: string | null; name_es?: string | null; name_en?: string | null; is_estate: boolean }>;
  estate_groups: Array<{ id: number; name: string; has_admin_business: boolean }>;
}

const BASE = '/sam/business-requests';

/** httpClient hands back the body, not the axios response. */
const call = async <T>(config: AxiosRequestConfig): Promise<T> => (await directApiInstance(config)) as unknown as T;

/** Requests whose failure the page shows itself (httpClient skips its generic toast). */
const quiet = (config: AxiosRequestConfig): AxiosRequestConfig => ({ ...config, quietErrors: true } as AxiosRequestConfig);

const enc = encodeURIComponent;

export const businessRequestsApi = {
  list: async (params: { mine?: boolean } = {}) =>
    (await call<{ requests?: BusinessRequest[] }>(quiet({
      url: BASE, method: 'GET', params: params.mine ? { mine: 1 } : { limit: 100 },
    }))).requests ?? [],
  get: async (id: string) => (await call<{ request: BusinessRequest }>(quiet({ url: `${BASE}/${enc(id)}`, method: 'GET' }))).request,
  approve: (id: string) => call<{ request: BusinessRequest }>(quiet({ url: `${BASE}/${enc(id)}/approve`, method: 'POST', data: {} })),
  reject: (id: string, note: string) => call<{ request: BusinessRequest }>({ url: `${BASE}/${enc(id)}/reject`, method: 'POST', data: { note } }),
  create: async (body: { type: RequestType; kind: RequestKind; target_id?: string } & Record<string, unknown>) =>
    (await call<{ request: BusinessRequest }>(quiet({ url: BASE, method: 'POST', data: body }))).request,
  options: () => call<RequestOptions>(quiet({ url: `${BASE}/options`, method: 'GET' })),
};
